// The "join this channel" prompt. The server decides when (ChannelJoinPrompts):
// a non-member's posts and upvalues in an open channel queue a prompt at the
// 1st, 10th and 100th, and it hands that channel's prompt out, at most once a
// day per channel, as `join_invite` on the post/upvalue response for that channel.
// The client only skips channels it already knows the user joined, and words it.
export type ChannelJoinInvite = {
    team_id?: string | null
    team_name?: string | null
    milestone?: number | null
    activity_count?: number | null
    trigger?: 'post' | 'upvalue' | string | null
    // True when the action this answers is what queued it ("you just ...").
    fresh?: boolean | null
}

export type ShownChannelJoinInvite = {
    teamId: string
    teamName: string
    milestone: number
    activityCount: number
    trigger: string
    fresh: boolean
    // Set when the card is opened by the user's own action (e.g. New thread in a
    // members-only channel) rather than by the prompt queue.
    reasonText?: string
    afterJoin?: () => void
    // The full channel, when the opener has it (keeps posting rules on join).
    team?: { id: string | number; name?: string; [key: string]: unknown }
}

const JOIN_BENEFIT = 'Join to see its threads in your Following.'

export const membersOnlyPostReason = (teamName: string) =>
    `Only members can post in ${teamName}. Join to post here and see its threads in your Following.`

export const channelJoinInviteReason = (invite: ShownChannelJoinInvite) => {
    if (invite.reasonText) return invite.reasonText
    const { teamName, milestone, activityCount, trigger, fresh } = invite
    if (milestone <= 1) {
        if (fresh && trigger === 'upvalue')
            return `You just upvalued a post in ${teamName}. ${JOIN_BENEFIT}`
        if (fresh) return `You just posted in ${teamName}. ${JOIN_BENEFIT}`
        return `You've been taking part in ${teamName}. ${JOIN_BENEFIT}`
    }
    const count = activityCount >= 100 ? '100+' : String(Math.max(activityCount, milestone))
    return `You've posted or upvalued in ${teamName} ${count} times. ${JOIN_BENEFIT}`
}

type ChannelJoinInviteDeps = {
    isMember: (teamId: string) => boolean
    show: (invite: ShownChannelJoinInvite) => void
}

export const createChannelJoinInvite =
    (deps: ChannelJoinInviteDeps) => (invite: ChannelJoinInvite | null | undefined) => {
        if (!invite?.team_id) return
        const teamId = String(invite.team_id)
        if (deps.isMember(teamId)) return

        deps.show({
            teamId,
            teamName: invite.team_name || 'this channel',
            milestone: Number(invite.milestone) || 1,
            activityCount: Number(invite.activity_count) || 0,
            trigger: invite.trigger || 'post',
            fresh: !!invite.fresh,
        })
    }
